/* eslint-disable max-lines-per-function */
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import ColoredDot from '@/domain/termine/components/ColorPicker/ColorDot';
import {
  markierungGermanMap,
  markierungMapper,
} from '@/domain/termine/shared/types/Markierung';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

const enToDeMap: Record<string, string> = Object.entries(
  markierungGermanMap,
).reduce((acc, [de, en]) => ({ ...acc, [en]: de }), {});

const colorEntries = Object.entries(markierungMapper);

interface ColorPickerDrawerProps {
  value: string;
  onChange: (markierung: string) => void;
}

export function ColorPickerDrawer({ value, onChange }: ColorPickerDrawerProps) {
  const { t } = useTranslation();

  const currentColor = markierungMapper[value]?.base;
  const currentLabel = enToDeMap[value] ?? value;

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <div
          className={cn(
            'flex items-center justify-between w-20 px-2 py-1 ',
            'rounded-md border border-neutrals-N300 cursor-pointer',
          )}
          tabIndex={0}
          role="button"
          aria-label={currentLabel}>
          <ColoredDot color={currentColor} title={currentLabel} />
          <FontAwesomeIcon
            icon={faChevronDown}
            className="ml-2 text-neutrals-N400"
          />
        </div>
      </DrawerTrigger>
      <DrawerContent className="flex flex-col max-h-[90vh]">
        <DrawerHeader className="px-3 pb-0">
          <DrawerTitle className="text-sm text-center text-neutrals-N900 font-semibold pt-2">
            {t('domain.termine.form.fields.farbe')}
          </DrawerTitle>
          <DrawerDescription className="sr-only">
            {t('domain.termine.form.fields.farbe')}
          </DrawerDescription>
        </DrawerHeader>
        <ScrollArea className="flex-1 overflow-auto p-4">
          {colorEntries.map(([name, { base }], idx) => {
            const isSelected = value === name;
            return (
              <div key={name} className="flex flex-col items-center w-full">
                <Button
                  variant="ghost"
                  aria-selected={isSelected}
                  onClick={() => onChange(name)}
                  className="w-full justify-between hover:bg-transparent">
                  <div className="flex items-center gap-3">
                    <ColoredDot color={base} />
                    <span className="text-sm font-medium">
                      {enToDeMap[name] ?? name}
                    </span>
                  </div>
                  {isSelected && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-primary-blue-B600 ml-auto"
                    />
                  )}
                </Button>
                {idx < colorEntries.length - 1 && (
                  <div className="border-b border-neutrals-N300 w-full" />
                )}
              </div>
            );
          })}
        </ScrollArea>
        <div className="flex justify-center p-4 flex-shrink-0">
          <DrawerClose asChild>
            <Button className="w-full" size="lg">
              {t('domain.termine.form.fertig')}
            </Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
